import { DisplaySubMetric } from '@repo/types';
import { calculatePreviewChartContext } from '@repo/utils';
import c from 'classnames';
import { Icon, Typography } from 'components';
import PreviewChart from 'components/PreviewChart/PreviewChart';
import { userValueFormat } from 'helpers';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { Link } from 'react-router-dom';
import { ActionPlanItem } from 'types/ActionPlan';
import { EvaluationPillFromColor } from '../../SubMetricGraphs/SubMetricGraphs';
import CategoryTags from './CategoryTags';
import styles from './MetricsCategories.module.css';

type SubMetricRowProps = {
  submetric: DisplaySubMetric;
  allActions: ActionPlanItem[];
  topPriorityRanks: Map<number, number>;
  // Keeps the value aligned with sibling rows that render a progress chart.
  reserveChartSpace: boolean;
};

// One metric line item: evaluation icon, name, value, progress sparkline and a
// chevron. Used by the All Metrics list and the Related metrics module.
export default function SubMetricRow({
  submetric,
  allActions,
  topPriorityRanks,
  reserveChartSpace,
}: SubMetricRowProps) {
  const kitId = useGetKitIdParam();

  const parsedValue = userValueFormat(
    submetric.user_value,
    submetric.data_type,
    submetric.user_string,
  );
  const unit = submetric?.gut_vaginal_health?.unit;
  const withUnitValue = `${parsedValue}${unit ? ` ${unit}` : ''}`;

  const previewChartContext = calculatePreviewChartContext(submetric);

  const actions = getActionsForSubMetric(
    allActions,
    submetric.sample_health_id,
  );
  const strains = new Set(
    (submetric.gut_vaginal_health_strain_kits ?? []).map(
      strain => strain.strain_name,
    ),
  ).size;

  return (
    <Link
      to={`/results/${kitId}/metrics/all-metrics/${submetric.sample_health_id}`}
      className={styles.submetric}
    >
      <EvaluationPillFromColor
        evaluation_color={submetric.evaluation_color}
        showIconOnly
      />
      <div className={styles.leftSide}>
        <div className={styles.nameContainer}>
          <Typography variant='body-s' color='dark' className={styles.name}>
            {submetric.name}
          </Typography>
          <CategoryTags
            submetric={submetric}
            topPriorityRanks={topPriorityRanks}
            className='hide-on-mobile'
          />
        </div>
        {actions.length > 0 || strains > 0 ? (
          <div className={styles.additionalInfo}>
            {actions.length > 0 && (
              <Typography variant='caption' color='grey'>
                {actions.length} recommended action
                {actions.length > 1 ? 's' : ''}
              </Typography>
            )}
            {strains > 0 && (
              <Typography variant='caption' color='grey'>
                {`${strains} strain${strains > 1 ? 's' : ''} detected`}
              </Typography>
            )}
            <CategoryTags
              submetric={submetric}
              topPriorityRanks={topPriorityRanks}
              className='hide-on-desktop'
            />
          </div>
        ) : null}
      </div>
      <div className={styles.valueContainer}>
        <Typography
          variant='body-s'
          className={c(
            styles.value,
            reserveChartSpace &&
              !previewChartContext.shouldRender &&
              styles.reserveChartSpace,
          )}
          color={submetric.evaluation_color}
        >
          {withUnitValue}
        </Typography>
        {previewChartContext.shouldRender && (
          <PreviewChart
            context={previewChartContext}
            className='hide-on-mobile'
          />
        )}
        <Icon name='chevronForward' size='s' className={styles.chevron} />
      </div>
    </Link>
  );
}
